import type { CaptureConstraints } from "@cuekards/core/media-devices";
import type { CaptureCommand, CaptureEvent } from "@cuekards/core/recording";
import {
  captureFailureMessage,
  chooseCaptureMimeType,
  micFailureMessage,
} from "@cuekards/core/recording";

/** One chunk as `MediaRecorder` hands it over. */
export interface BlobLike {
  readonly size: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export interface TrackLike {
  /**
   * `"audio"` or `"video"`, as `MediaStreamTrack.kind` gives it — typed as the
   * wider `string` so a real `MediaStream` still satisfies
   * {@link MediaStreamLike} structurally.
   *
   * It is here so the engine can hand one device back and keep the other: turning
   * the camera off in the green room stops the video track where it stands rather
   * than closing and reopening a microphone that was never in question.
   */
  readonly kind: string;
  stop(): void;
}

/**
 * The slice of `MediaStream` the engine uses.
 *
 * It grew a track list you can *edit* in web-ui ticket 06, and that is the whole
 * of how a device comes back on without the other one being disturbed. Turning
 * the camera off already stopped the video track where it stood; turning it back
 * on used to close the standby and open both kinds again — a new stream identity
 * for the host's `<video>` to re-bind to, a `listening: false` / `true` blip, and
 * a microphone reopened for no reason at all. With these two, only the missing
 * kind is opened and its track is grafted into the stream already in hand, so the
 * identity is stable across every toggle and the microphone is never touched.
 *
 * {@link MediaStreamLike.removeTrack} earns its place twice over: a merely
 * `stop()`ed track stays in `getTracks()`, so a host reading the stream for "is
 * there a picture" would go on seeing one, and a `<video>` bound to it would sit
 * on a frozen last frame rather than going away.
 */
export interface MediaStreamLike {
  /** A snapshot, as `MediaStream.getTracks()` gives it — the engine removes
   * tracks while walking this, which is only safe because it is a copy. */
  getTracks(): TrackLike[];
  addTrack(track: TrackLike): void;
  removeTrack(track: TrackLike): void;
}

/**
 * The slice of `MediaRecorder` this engine uses, as a port. The event handlers
 * are methods rather than assignable properties so the whole thing can be
 * faked in Node, where `MediaRecorder` does not exist.
 */
export interface RecorderLike {
  /** The container the recorder actually settled on, which is not necessarily
   * the one that was asked for. */
  readonly mimeType: string;
  start(timesliceMs: number): void;
  pause(): void;
  resume(): void;
  stop(): void;
  onDataAvailable(listener: (chunk: BlobLike) => void): void;
  onStop(listener: () => void): void;
  onError(listener: (error: unknown) => void): void;
}

/**
 * What one `getUserMedia` call is asked for — one signature, because there is one
 * call site (FR-31).
 *
 * Both keys are optional, and **an absent key means that device is not wanted at
 * all**: the follower's rehearsal asks for the microphone alone, and the green
 * room's camera-off and microphone-off states are each the corresponding key going
 * away. `CaptureConstraints` is assignable to it, so a take's constraints flow
 * through untouched — and a *take* can arrive with the video key already absent
 * too, on a machine with no camera, because asking for one there is refused
 * outright and takes the microphone with it.
 */
export type OpenConstraints = Partial<CaptureConstraints>;

export interface CaptureEnginePorts {
  getUserMedia(constraints: OpenConstraints): Promise<MediaStreamLike>;
  createRecorder(
    stream: MediaStreamLike,
    mimeType: string | null,
  ): RecorderLike;
  isTypeSupported(mimeType: string): boolean;
  /**
   * The containers this host wants, most preferred first — the desktop's list by
   * default (see `CAPTURE_MIME_PREFERENCES`).
   *
   * A port rather than a constant because the right container is a property of
   * the *host* and not of the browser (web ticket 01). The desktop is going to
   * run a stream copy on stop whatever it captured, so it takes Matroska and buys
   * FR-45's one-second bound on what a crash costs. A tab has no converter and no
   * way to get one, so it takes the direct-mux MP4 and pays the coarser chunk
   * cadence, because the alternative is handing someone a `.mkv` and an apology.
   */
  mimePreferences?: readonly string[];
  /**
   * Fork the microphone: attach a read-only consumer to an already-open stream
   * and return the function that detaches it (FR-31).
   *
   * "Read-only" is the requirement, not a description. The tap observes the same
   * audio track the recorder is recording — it does not clone it, re-open the
   * device, or sit between the track and the recorder — so nothing it does can
   * reach the take. That is what makes recorded audio byte-for-byte unaffected by
   * the fork rather than merely unaffected in practice.
   */
  tapAudio(stream: MediaStreamLike): () => void;
  send(event: CaptureEvent): void;
  /**
   * The stream the host may put on screen right now, or null when there is none
   * (web-ui ticket 04).
   *
   * A port rather than a {@link CaptureEvent} for two reasons. A `MediaStream` is
   * not serialisable, so it could not cross the desktop's process boundary even if
   * the contract wanted it — this is the one fact about capture that belongs to
   * the document rather than to main. And it is the *displayable* stream, not the
   * standby: when a take cannot borrow the preview and opens its own devices, this
   * is what stops the self-view freezing on the last frame the preview gave it.
   *
   * Optional, and never called at all on a host that has not asked for a preview,
   * so the desktop — which never does — sees none of this.
   */
  previewChanged?(stream: MediaStreamLike | null): void;
  /** Why the preview could not be opened. Never fails a take — there isn't one. */
  previewFailed?(message: string): void;
}

interface Session {
  id: string;
  stream: MediaStreamLike;
  recorder: RecorderLike;
  /** The chunk interval `go` will start the recorder with, held from `open`. */
  timesliceMs: number;
  /** Whether `go` has arrived. A recorder that has not been started cannot be
   * paused or stopped — `MediaRecorder` throws `InvalidStateError` — and this is
   * the window in which the countdown is running. */
  started: boolean;
  /** Detaches the follower's fork from this take's audio. A no-op when the take
   * borrowed the standby, which owns the fork on that stream. */
  untap: () => void;
  /**
   * Whether this take is recording the standby's own stream rather than one it
   * opened for itself.
   *
   * A borrowed take must not stop the tracks when it ends, and must not fork the
   * microphone a second time — the tap has been on that stream since the camera
   * came up. Deliberately *not* mirrored by a flag on the standby: there is no
   * `await` between the borrow decision in `#open` and the session being
   * assigned, so the fact is derivable from the session, and a second copy of it
   * would be left stuck at `true` by a `createRecorder` that throws — a camera
   * nothing could then turn off.
   */
  borrowed: boolean;
}

/**
 * The devices held open with no take in progress: the follower's rehearsal
 * microphone (FR-37), the green room's self-view (web-ui ticket 04), or both at
 * once over one stream.
 *
 * One holder rather than two, because two would mean two `getUserMedia` calls for
 * the same microphone — precisely the exclusive-mode contention FR-31 exists to
 * forbid.
 */
interface Standby {
  stream: MediaStreamLike;
  /**
   * The follower's fork, or null when the devices are open but nothing is
   * listening to them.
   *
   * Open and forked are two facts here, where they used to be one. The self-view
   * wants the microphone *open* — so a take can borrow it, and so turning it off
   * is a real thing to do; rehearsal wants it *tapped*. Keeping them apart is what
   * makes a `listen` arriving over a live preview cost a `tapAudio` call and no
   * device open at all.
   */
  untap: (() => void) | null;
  /** What it is holding live, which is what a take compares against to decide
   * whether it may borrow it rather than reopen the camera. */
  holding: OpenConstraints;
}

/**
 * The recording engine's half in the document (ticket 12).
 *
 * It exists here and nowhere else because `getUserMedia` and `MediaRecorder`
 * are web APIs, and on the desktop the process that owns the state is not the one
 * that has them. So this is a driver — it holds no decisions, only hardware. The
 * host says start, pause, resume or stop; this opens the devices, drives the one
 * recorder, and reports what happened.
 *
 * Two things it does own, because only it can:
 *
 * - **The container.** `MediaRecorder.isTypeSupported` is a browser API, so the
 *   choice is made here and reported back, since what happens to the capture
 *   afterwards is keyed on what was actually recorded. *Which* order is
 *   preferred is the host's — see {@link CaptureEnginePorts.mimePreferences}.
 * - **Chunk ordering.** Reading a `Blob` is asynchronous, so two chunks read
 *   concurrently can finish in either order — and a capture file assembled out
 *   of order is not a capture file. Every chunk read, and the `stopped` event
 *   that follows the last of them, goes through one sequential queue.
 *
 * Opening the devices and rolling are two commands (`open`, then `go`) rather
 * than one, because that is what makes FR-44's countdown honest: opening a webcam
 * takes seconds, so it happens while the numbers are on screen, and `go` — which
 * costs nothing but `MediaRecorder.start` — is what the countdown's last tick
 * triggers.
 *
 * ## The microphone fork (ticket 14, FR-31)
 *
 * The PRD states as an *invariant* that the microphone is opened exactly once and
 * the stream forked to two consumers — recorder and follower — because opening it
 * twice risks exclusive-mode contention and drift between the streams, and the
 * failure would appear as damaged audio in a take that cannot be redone (risk R3).
 *
 * That invariant is structural here rather than a rule two modules have to keep,
 * in two parts:
 *
 * 1. **This engine is the only thing in the app that calls `getUserMedia`.** One
 *    call opens camera and microphone together, and the follower is forked off that
 *    same audio track by `tapAudio` — never by a second open. Rehearsal (FR-37),
 *    which wants the microphone with no take at all, therefore also goes through
 *    here as `listen`/`unlisten` rather than opening its own — and so does the
 *    web's self-view, as {@link CaptureEngine.preview}.
 * 2. **Every command that opens the device goes through one queue** — see
 *    `#opens`. Commands arrive fire-and-forget, so being the only caller is not
 *    sufficient on its own: two of this engine's own opens could otherwise
 *    overlap, and each would see the other's device as not yet open.
 *
 * With both, a take that interrupts a standby closes its microphone before opening
 * its own and reopens it afterwards, and no two opens are ever in flight at once.
 *
 * ## The preview a take borrows (web-ui ticket 04)
 *
 * The browser app shows you your own face before you record, which means the camera
 * is open for the whole time you are deciding what to say. That is the same
 * mechanism as rehearsal with a video track added — see {@link Standby} — plus one
 * rule that is new:
 *
 * **A take borrows the standby rather than reopening the device.** `#open` used to
 * close the standby unconditionally, which for a microphone costs nothing visible;
 * doing it to a camera would blink the recording light and cost a black frame at
 * the exact moment the countdown ends. So when the standby is already holding
 * precisely what the take asked for, the take records that very stream, and giving
 * the hardware back stops being the take's decision — the preview outlives it.
 */
export class CaptureEngine {
  readonly #ports: CaptureEnginePorts;
  #session: Session | null = null;
  /** The take being opened, if any — held separately from {@link CaptureEngine}'s
   * session because opening takes time the user can press stop during. */
  #opening: string | null = null;
  #aborted = false;
  /** The sequential queue every chunk and terminal event goes through. */
  #queue: Promise<void> = Promise.resolve();
  /** The devices held open with no take, when any are (FR-37, web-ui ticket 04). */
  #standby: Standby | null = null;
  /**
   * The constraints rehearsal was asked for, held for as long as the user wants
   * it — including across a take, which borrows the microphone and gives it back.
   * Null when rehearsal has not been asked for or has been cancelled.
   */
  #rehearsalWanted: MediaTrackConstraints | null = null;
  /** What the host wants held open for the self-view, or null for nothing. */
  #previewWanted: OpenConstraints | null = null;
  /** The preview request the hardware turned down, so asking for it again is not
   * mistaken for asking for something new. Cleared by a request that differs, and
   * by clearing the preview — see {@link CaptureEngine.preview}. */
  #previewRefused: OpenConstraints | null = null;
  /**
   * Whether a preview was ever asked for. Gates
   * {@link CaptureEnginePorts.previewChanged} entirely, so a host that does not
   * use one — the desktop — is never called back about a stream it has no use for.
   */
  #previewAsked = false;
  /** The stream last reported to the host, so an unchanged one is not re-announced
   * into a render loop. */
  #reported: MediaStreamLike | null = null;
  /**
   * How many times what is wanted has been changed, by anybody (web-ui ticket 06).
   *
   * A reconcile that has been away at the hardware has to know whether anyone is
   * still waiting for the answer it came back with, and it used to ask that by
   * comparing the constraints it set out with against the ones wanted now. That
   * is the right question asked in a way that cannot answer it: **a retry asks
   * for exactly what was just refused**, so a press that landed while the
   * previous rejection was still in flight was indistinguishable from the request
   * that was failing — the catch saw its own constraints, believed nothing had
   * moved, and cleared the request the user had just made. A lost press rather
   * than a dead button, and only for the length of one refused `getUserMedia`,
   * but nothing structural stopped it happening.
   *
   * What separates those two requests is *when* they were made, so that is what
   * is compared. It costs one open in the rare case where a want returns to what
   * an in-flight open is already fetching — and it retires the whole class of
   * "has the request moved on" reasoning rather than this one instance of it.
   */
  #generation = 0;
  /**
   * The queue every command that opens the microphone goes through, one at a
   * time.
   *
   * This is what makes FR-31's invariant hold rather than merely look like it
   * holds. Commands arrive from the host fire-and-forget, so two can be in
   * flight at once — and `getUserMedia` takes real time, seconds for a cold
   * webcam. Without a queue, "is a microphone already open?" is answered `false`
   * by both of two overlapping opens, and the device ends up open twice: exactly
   * the exclusive-mode contention and stream drift risk R3 is about. Guard flags
   * cannot fix that on their own, because the second command must still be
   * *carried out*, just not concurrently.
   *
   * Only the opening commands are queued. `stop`, `pause`, `resume` and
   * `unlisten` stay immediate, because a stop that waited behind a slow camera
   * open would leave the user unable to cancel for as long as the hardware takes.
   */
  #opens: Promise<void> = Promise.resolve();

  constructor(ports: CaptureEnginePorts) {
    this.#ports = ports;
  }

  /** Whether the camera is open or about to be. */
  get capturing(): boolean {
    return this.#session !== null || this.#opening !== null;
  }

  /**
   * Resolves once nothing is in flight — every queued chunk handed to main, and
   * every queued device open finished.
   *
   * Loops rather than awaiting both once, because the two queues feed each other:
   * a take ending on the chunk queue can enqueue a standby reopening on the open
   * queue, and vice versa.
   */
  async whenIdle(): Promise<void> {
    let queue: Promise<void>;
    let opens: Promise<void>;
    do {
      queue = this.#queue;
      opens = this.#opens;
      await Promise.all([queue, opens]);
    } while (queue !== this.#queue || opens !== this.#opens);
  }

  /** Carry out one command from main. Anything that does not apply in the
   * current state is ignored — main is the authority on the take, and it
   * having a different idea is not something to act on halfway. */
  async handle(command: CaptureCommand): Promise<void> {
    switch (command.kind) {
      case "open":
        // Claimed now, synchronously, for the same reason `listen` records its
        // intent now: the queue defers the actual open, and a `stop` arriving in
        // between has to find a take to cancel. Without this the stop would be
        // dropped and the camera would roll on a take the user had abandoned.
        if (this.capturing) return;
        this.#opening = command.sessionId;
        this.#aborted = false;
        return this.#queueOpen(() => this.#open(command));
      case "listen":
        // The *intent* is recorded now, synchronously, rather than when the queue
        // reaches it: an `unlisten` arriving in between has to be able to cancel a
        // rehearsal that has not opened yet, or the microphone would come on after
        // the user had already turned it off.
        this.#rehearsalWanted = command.audio;
        this.#generation++;
        return this.#queueOpen(() => this.#reconcile());
      case "go":
        this.#go(command.sessionId);
        return;
      case "pause":
        // Only a rolling recorder can be paused; during the countdown there is
        // one built and waiting, and pausing that throws.
        if (this.#session?.started) this.#session.recorder.pause();
        return;
      case "resume":
        if (this.#session?.started) this.#session.recorder.resume();
        return;
      case "stop":
        this.#stop();
        return;
      case "unlisten":
        this.#unlisten();
        return;
    }
  }

  /**
   * Hold the camera and microphone open with no take in progress, so the host can
   * put the stream on screen — or pass null to close it (web-ui ticket 04).
   *
   * A method rather than a {@link CaptureCommand}, because the self-view is a
   * property of the surface showing it rather than of the recording session main
   * owns: the contract carries no `MediaStream` and has no reason to learn one.
   *
   * Turning one device off is the corresponding key going away, not a flag — see
   * {@link OpenConstraints} — and the *shape* of a preview that is wanted is
   * authoritative over rehearsal's standing request, so a microphone the user
   * has switched off in the green room cannot be reopened by the follower.
   */
  preview(constraints: OpenConstraints | null): Promise<void> {
    const wanted = normalise(constraints);
    if (wanted === null) {
      // Asking for nothing also forgets a refusal. That is what makes a retry
      // possible at all: a host clears the preview and asks again — which is
      // exactly what its "Try again" button is — and gets a fresh attempt.
      this.#previewRefused = null;
      if (this.#previewWanted === null) return Promise.resolve();
      this.#previewWanted = null;
      this.#generation++;
      this.#shrink();
      return this.#queueOpen(() => this.#reconcile());
    }
    // Hosts hand this over from a render, so an unchanged request arrives many
    // times a second. It costs one comparison rather than a queued device open.
    //
    // The second half of that comparison is what stops a refused camera becoming
    // a spin: reporting the refusal re-renders the host, the render asks again,
    // and without this the ask would look new every time. A request the hardware
    // has already turned down is not retried because a render happened.
    if (same(wanted, this.#previewWanted) || same(wanted, this.#previewRefused)) {
      return Promise.resolve();
    }
    this.#previewRefused = null;
    this.#previewAsked = true;
    this.#previewWanted = wanted;
    this.#generation++;
    // Hardware goes back now rather than behind the queue, for the reason
    // `unlisten` is immediate: a camera the user has turned off must not stay lit
    // for as long as some other open takes.
    this.#shrink();
    return this.#queueOpen(() => this.#reconcile());
  }

  /** Run one device-opening step once every earlier one has finished. A step that
   * throws must not break the chain for the next take. */
  #queueOpen(work: () => Promise<void>): Promise<void> {
    const run = this.#opens.then(work);
    this.#opens = run.catch(() => {});
    return run;
  }

  /** Open the devices for a take. Runs only from `#queueOpen`, with `#opening`
   * already claimed by {@link handle}. */
  async #open(
    command: Extract<CaptureCommand, { kind: "open" }>,
  ): Promise<void> {
    // Stopped before the queue even reached this take. Nothing is open, but main
    // is waiting to be told the take is over.
    if (this.#aborted) {
      this.#opening = null;
      this.#ports.send({ kind: "stopped", sessionId: command.sessionId });
      this.#reopenStandby();
      return;
    }

    // Borrow the standby when it is already holding exactly what this take wants.
    // The equality is the precondition and not an assumption: if the camera was
    // changed in settings since the preview came up, the standby is the *old*
    // device, and recording it would be recording the wrong shot. A mismatch falls
    // through to closing and reopening, which is what this always did.
    const standby = this.#standby;
    const borrowed =
      standby !== null && same(standby.holding, command.constraints);

    /**
     * Whether there is a microphone in this take at all.
     *
     * The follower always runs while recording (FR-37) — but that was written when
     * every take had audio in it, and since web-ui ticket 06 one can be started
     * with the microphone switched off. Forking a video-only stream attaches
     * nothing: `createMediaStreamSource` throws on a stream with no audio track,
     * and both hosts' taps catch and log it. So the fork appears to have worked,
     * main is told `listening: true`, and the transport reads "Following" for the
     * whole take — while the green room's own `MIC_OFF_NOTE` says the prompter
     * cannot follow your voice, two inches away.
     *
     * Read off the constraints rather than off the stream, because a take that
     * borrows the preview opens nothing and there is nothing else to ask.
     */
    const listens = command.constraints.audio !== undefined;

    let stream: MediaStreamLike;
    if (borrowed && standby) {
      stream = standby.stream;
      // If the preview was holding the microphone with nobody listening to it,
      // this is where the fork goes on — and the standby keeps ownership of it, so
      // the take ending does not detach a fork the preview still wants.
      if (listens && standby.untap === null) standby.untap = this.#tap(stream);
    } else {
      // Hand the standby back *before* asking for the take's devices, so nothing
      // is ever open twice even momentarily. The take's own audio is what the
      // follower listens to from here.
      this.#closeStandby();
      try {
        stream = await this.#ports.getUserMedia(command.constraints);

      /**
       * The take just opened these devices, which is evidence that a refusal
       * remembered about them is stale (web-ui ticket 06).
       *
       * `#previewRefused` is what stops a camera the hardware turned down being
       * asked for again on every render: reporting the failure re-renders the host,
       * the host asks again, and without the memory it becomes an open per frame.
       * It is cleared by asking for something *else*, and by `preview(null)`, which
       * is what the green room's Try again is. Neither of those happens here — the
       * host goes on repeating the same request every render, and it matches.
       *
       * So a preview refused while another application held the camera stayed
       * refused for the rest of the visit, although the take that is starting is
       * proof the camera is free. `#enqueue`'s `#report(null)` on the way out
       * clears the message at the same moment, which leaves the room on `opening`:
       * no picture, no reason, and no Try again to recover through.
       *
       * Only where the take covers what was refused. A take that opened the
       * microphone alone is no evidence at all about a camera somebody else is
       * still holding.
       */
      const refused = this.#previewRefused;
      if (
        refused !== null &&
        (same(command.constraints, refused) ||
          subsumes(command.constraints, refused))
      ) {
        this.#previewRefused = null;
      }
      } catch (error) {
        this.#opening = null;
        this.#ports.send({
          kind: "failed",
          sessionId: command.sessionId,
          message: captureFailureMessage(error),
        });
        this.#reopenStandby();
        return;
      }

      // Stop arrived while the camera was opening. Nothing was recorded, but the
      // hardware is now open and has to be handed back.
      if (this.#aborted) {
        this.#opening = null;
        release(stream);
        this.#ports.send({ kind: "stopped", sessionId: command.sessionId });
        this.#reopenStandby();
        return;
      }
    }

    let recorder: RecorderLike;
    try {
      recorder = this.#ports.createRecorder(
        stream,
        chooseCaptureMimeType(
          (type) => this.#ports.isTypeSupported(type),
          this.#ports.mimePreferences,
        ),
      );
    } catch (error) {
      this.#opening = null;
      // A borrowed stream belongs to the preview, and the preview did not fail.
      if (!borrowed) release(stream);
      this.#ports.send({
        kind: "failed",
        sessionId: command.sessionId,
        message: captureFailureMessage(error),
      });
      this.#reopenStandby();
      return;
    }

    const session: Session = {
      id: command.sessionId,
      stream,
      recorder,
      timesliceMs: command.timesliceMs,
      started: false,
      // Forked here rather than on `go`, so the follower is already listening
      // when the countdown ends and the first spoken word is not missed. A
      // borrowed stream was forked earlier still, when the preview opened, and
      // the standby is what detaches it.
      untap: borrowed || !listens ? () => {} : this.#tap(stream),
      borrowed,
    };
    recorder.onDataAvailable((chunk) => this.#onChunk(session, chunk));
    recorder.onStop(() =>
      this.#enqueue(session, { kind: "stopped", sessionId: session.id }),
    );
    recorder.onError((error) =>
      this.#enqueue(session, {
        kind: "failed",
        sessionId: session.id,
        message: captureFailureMessage(error),
      }),
    );

    this.#session = session;
    this.#opening = null;
    // A take that could not borrow the preview opened its own camera, and that is
    // now the only live stream there is — so it is the one the host may display,
    // or the self-view would sit on the last frame the preview gave it for the
    // whole take. A borrowed stream is already the reported one, and this is a
    // no-op.
    this.#report(stream);
    // The container is known now, before anything is rolling, which is what lets
    // main have the chunk file open by the time the countdown ends.
    this.#ports.send({
      kind: "opened",
      sessionId: session.id,
      mimeType: recorder.mimeType,
    });
  }

  /**
   * Roll. This is the countdown's last tick arriving, and it is deliberately the
   * cheapest thing in the engine — everything slow already happened during the
   * numbers.
   */
  #go(sessionId: string): void {
    const session = this.#session;
    // A `go` for a take this window is not holding would attach the wrong
    // session's footage to the file main is writing.
    if (!session || session.id !== sessionId || session.started) return;
    session.started = true;
    // The timeslice is what makes this a stream of chunks rather than one blob
    // at the end — the whole of FR-45 in one argument.
    session.recorder.start(session.timesliceMs);
    this.#ports.send({ kind: "started", sessionId: session.id });
  }

  #stop(): void {
    if (this.#opening !== null) {
      // Too early to stop a recorder that does not exist yet; the open path
      // checks this flag once the camera finishes opening.
      this.#aborted = true;
      return;
    }
    const session = this.#session;
    if (!session) return;
    if (!session.started) {
      // Cancelled during the countdown. Nothing was recorded, so there is no
      // flush to wait for — but the camera is open and has to be handed back.
      this.#enqueue(session, { kind: "stopped", sessionId: session.id });
      return;
    }
    session.recorder.stop();
  }

  #onChunk(session: Session, chunk: BlobLike): void {
    if (chunk.size === 0) return;
    this.#queue = this.#queue.then(async () => {
      const data = new Uint8Array(await chunk.arrayBuffer());
      this.#ports.send({ kind: "chunk", sessionId: session.id, data });
    });
  }

  /** Queue a terminal event behind the chunks already in flight, then release
   * the hardware. Ordering matters: the last chunk is footage, and it must
   * reach main before main is told the take is over. */
  #enqueue(session: Session, event: CaptureEvent): void {
    this.#queue = this.#queue.then(() => {
      if (this.#session === session) this.#session = null;
      if (session.borrowed) {
        // The take was recording the standby's own stream, so whether the
        // hardware goes back is not its decision: the preview keeps it if it is
        // still wanted, and gives it back here — behind the chunk queue, and
        // before `stopped` — if it is not. Either way the fork is settled first,
        // so main is never told a take is over while the engine still claims to
        // be listening through one it has already dropped.
        if (wanting(this.#wanted())) this.#settleTap();
        else this.#closeStandby();
      } else {
        session.untap();
        release(session.stream);
        this.#ports.send({ kind: "listening", listening: false });
        this.#report(null);
      }
      this.#ports.send(event);
      // The take borrowed the microphone from a standby in progress; give it
      // back, so stopping a take mid-rehearsal returns to rehearsing rather than
      // to silence, and the self-view comes back up rather than staying dark.
      this.#reopenStandby();
    });
  }

  /**
   * What ought to be open with no take in progress.
   *
   * A preview that is wanted is authoritative — including about the microphone it
   * has switched *off* — because it is the thing the user is looking at; rehearsal
   * only gets a say when there is no preview at all.
   *
   * **"No preview at all" is null and nothing else.** A preview of `{}` is a host
   * that wants one and has every device switched off, and it wins here the same
   * way any other shape does — see `normalise`, where collapsing the two cost
   * exactly this rule the moment the second switch went off. The explicit null
   * test below is what reads `{}` as the request it is rather than as an absence —
   * a truthiness test here would have read it as one again.
   */
  #wanted(): OpenConstraints {
    if (this.#previewWanted !== null) return this.#previewWanted;
    return this.#rehearsalWanted ? { audio: this.#rehearsalWanted } : {};
  }

  /**
   * Bring the standby into line with what is wanted. Runs only from `#queueOpen`,
   * so no other open is in flight when it starts.
   *
   * Two rules hold this together and neither is negotiable. **It never queues
   * more work** — every change of want queues its own reconcile and the queue is
   * FIFO, so re-queuing from in here would spin {@link whenIdle} forever under an
   * oscillating want. And **it awaits one thing at a time** — two concurrent
   * opens for two device kinds is exactly the invariant that queue exists to
   * prevent, however tempting `Promise.all` looks.
   */
  async #reconcile(): Promise<void> {
    const want = this.#wanted();
    /* The state of the want at the moment this step set out. Everything after an
     * `await` in here is conditional on it — see {@link CaptureEngine.#generation}
     * for why it is a number rather than the constraints themselves. */
    const generation = this.#generation;
    // Read now rather than after the await: a request that changes while the
    // hardware is failing would otherwise have the *other* one cancelled and a
    // message about the wrong device sent in its name.
    const forPreview = this.#previewWanted !== null;
    if (!wanting(want)) {
      this.#closeStandby();
      return;
    }
    // A take has the devices. It borrowed the standby or replaced it; either way
    // the reconcile that follows the take is what puts this right.
    if (this.capturing) return;

    const held = this.#standby;
    /** The standby a newly opened track is grafted into, or null when a whole
     * stream is being opened from nothing. */
    let graftInto: Standby | null = null;
    if (held) {
      if (same(held.holding, want)) {
        this.#settleTap();
        return;
      }
      if (subsumes(held.holding, want)) {
        // A device was turned off. Stop that track where it stands: the other one
        // is not in question, and closing the stream to reopen it a kind smaller
        // would interrupt a microphone nobody asked about.
        this.#dropTracks(held, want);
        this.#settleTap();
        return;
      }
      // A device was turned back on. Only the missing kind is opened, and its
      // track is grafted into the stream already in hand — so the stream identity
      // the host is rendering from does not move, and the device that was never
      // in question is not reopened behind the one that was.
      if (subsumes(want, held.holding)) graftInto = held;
      else this.#closeStandby();
    }
    const opening =
      graftInto === null ? want : missingFrom(graftInto.holding, want);

    let stream: MediaStreamLike;
    try {
      stream = await this.#ports.getUserMedia(opening);
    } catch (error) {
      // The request moved on while the hardware was failing. Whatever this
      // message is about, nobody is waiting for it any more, and the reconcile
      // queued behind the change is what says what is true now.
      if (generation !== this.#generation) return;
      // Clear the standing request, or every take end from here on would retry a
      // device the user has refused. The host asks again when the user does.
      if (forPreview) {
        this.#previewWanted = null;
        this.#previewRefused = want;
        this.#ports.previewFailed?.(captureFailureMessage(error));
        // Rehearsal may still want the microphone this preview was going to
        // carry, and it was closed to make room for a camera that never came.
        // Tail-called rather than re-queued: it is this same slot in the open
        // queue, so no two opens overlap — and the want has strictly shrunk, so
        // there is no second time round.
        if (wanting(this.#wanted())) await this.#reconcile();
        return;
      }
      this.#rehearsalWanted = null;
      this.#ports.send({
        kind: "listenFailed",
        message: micFailureMessage(error),
      });
      return;
    }

    // A take started, or the request changed, while the devices were opening.
    // Either way this stream is not wanted and must not be left open — an
    // unreleased stream is an indicator light that never goes out. The standby
    // is checked against the one this step meant to graft into, which is null in
    // the ordinary case and so is the same reading as "nothing opened behind me".
    if (
      this.capturing ||
      this.#standby !== graftInto ||
      generation !== this.#generation
    ) {
      release(stream);
      return;
    }

    if (graftInto !== null) {
      // Off the donor before onto the host stream, so the release below has
      // nothing left to stop — a track in two streams that one of them hands
      // back is a track the other one is holding a corpse of.
      for (const track of stream.getTracks()) {
        stream.removeTrack(track);
        graftInto.stream.addTrack(track);
      }
      release(stream);
      graftInto.holding = want;
      this.#settleTap();
      // Said again although the stream is the one the host already has: what
      // changed is inside it, and a host that renders from `getTracks()` has no
      // other way to hear about that.
      this.#announce(graftInto.stream);
      return;
    }

    this.#standby = { stream, untap: null, holding: want };
    this.#settleTap();
    /**
     * Offered to the host only if a preview is what wanted it (web-ui ticket 06).
     *
     * A camera that failed while a rehearsal was standing gets here holding the
     * microphone alone — the tail call above, so that rehearsal survives a camera
     * it was sharing a stream with. That stream is not a preview: there is no
     * picture in it, and handing it over would replace the failure the host was
     * just told about with something it cannot show. On the green room that is no
     * picture, no message and no Try again — the one camera stage with nothing on
     * it, indefinitely, over a camera it could have named.
     *
     * Here and not inside `#report`, which is deliberate and was a bug once: a
     * take's own stream is reported from `#open` while `#previewWanted` may well
     * be null — the preview it failed to open is exactly what a refused
     * `#previewRefused` keeps null — and swallowing that one leaves the self-view
     * on a stale frame for the whole take.
     */
    if (this.#previewWanted !== null) this.#report(stream);
  }

  /**
   * Stop rehearsing. Deliberately does *not* unfork a take in progress — the
   * follower always runs while recording (FR-37), and dropping the tap mid-take
   * would freeze the prompter on a live performance. It only cancels the standing
   * request, so the rehearsal does not come back when the take ends — and cancels
   * one that is still queued, so a microphone the user has turned off cannot come
   * on a moment later.
   */
  #unlisten(): void {
    this.#rehearsalWanted = null;
    this.#shrink();
    void this.#queueOpen(() => this.#reconcile());
  }

  /**
   * Everything a *shrinking* want implies, at once rather than behind the queue.
   *
   * Only ever gives hardware back, which is what makes it safe to run ahead of
   * whatever is already opening: a device the user has just switched off must go
   * dark now, not when some unrelated camera has finished waking up. Anything
   * additive is left to the reconcile queued behind it.
   */
  #shrink(): void {
    const held = this.#standby;
    if (!held || this.#borrowing(held)) return;
    const want = this.#wanted();
    if (!wanting(want)) {
      this.#closeStandby();
      return;
    }
    if (same(held.holding, want)) {
      this.#settleTap();
      return;
    }
    if (subsumes(held.holding, want)) {
      this.#dropTracks(held, want);
      this.#settleTap();
    }
    // Anything else is a want that grew or changed shape, and that is not this
    // method's business. Closing the device here would darken a camera that is
    // about to be replaced by one very like it, for as long as the swap takes;
    // the reconcile queued behind this does it with the new stream in hand.
  }

  /** Attach or detach the follower's fork so it matches the standing rehearsal
   * request. The fork is not the device: a preview can hold the microphone with
   * nothing listening to it, and rehearsal can start over a microphone that is
   * already open without opening anything. */
  #settleTap(): void {
    const held = this.#standby;
    // A take is recording this stream; its fork is the take's until it ends.
    if (!held || this.#borrowing(held)) return;
    const wanted =
      this.#rehearsalWanted !== null && held.holding.audio !== undefined;
    if (wanted && held.untap === null) {
      held.untap = this.#tap(held.stream);
    } else if (!wanted && held.untap !== null) {
      held.untap();
      held.untap = null;
      this.#ports.send({ kind: "listening", listening: false });
    }
  }

  /**
   * Stop the tracks whose kind is no longer wanted, and leave the rest alone.
   *
   * Removed from the stream as well as stopped, which is not tidiness: a
   * `stop()`ed track stays in `getTracks()`, so a host reading the stream for "is
   * there a picture" would go on seeing one over a stage with nothing in it — and
   * the `<video>` still bound to it would hold the last frame it got rather than
   * going dark behind whatever is drawn instead.
   */
  #dropTracks(held: Standby, want: OpenConstraints): void {
    const keep = new Set<string>(kinds(want));
    for (const track of held.stream.getTracks()) {
      if (keep.has(track.kind)) continue;
      track.stop();
      held.stream.removeTrack(track);
    }
    held.holding = want;
    this.#announce(held.stream);
  }

  /**
   * Detach and release the standby if one is open, and say so.
   *
   * It announces rather than leaving that to the caller, because every caller
   * needs it said: the take that borrows the device announces it too, and going
   * quiet for the seconds a camera takes to open would leave the follower state
   * reading "Following" over a microphone that is shut.
   *
   * Refuses while a take is recording this very stream. That take is not finished
   * with it, and the reconcile that follows the take is what closes it then.
   */
  #closeStandby(): void {
    const held = this.#standby;
    if (!held || this.#borrowing(held)) return;
    this.#standby = null;
    if (held.untap) {
      held.untap();
      this.#ports.send({ kind: "listening", listening: false });
    }
    release(held.stream);
    this.#report(null);
  }

  /** Bring the standby back up behind whatever else is opening. */
  #reopenStandby(): void {
    void this.#queueOpen(() => this.#reconcile());
  }

  /** Whether a take is recording this standby's own stream. */
  #borrowing(held: Standby): boolean {
    const session = this.#session;
    return (
      session !== null && session.borrowed && session.stream === held.stream
    );
  }

  /** Tell the host what it may put on screen. Silent on a host that never asked
   * for a preview, and silent about a stream it has already been given. */
  #report(stream: MediaStreamLike | null): void {
    if (!this.#previewAsked || this.#reported === stream) return;
    this.#reported = stream;
    this.#ports.previewChanged?.(stream);
  }

  /**
   * Say the stream again although its identity has not moved: a track was
   * grafted into it, or one was taken out of it (web-ui ticket 06).
   *
   * Past {@link CaptureEngine.#report}'s guard on purpose. That guard is against
   * a *render loop* — a host told the same thing on every render — and this is
   * not that: it happens once per press on a button, and what it carries is the
   * only news there is, because the change is inside an object the host is
   * already holding.
   *
   * Not past the other one. A want that has shrunk to rehearsal's microphone gets
   * here too, by `#dropTracks`, and it is the same stream with the same nothing to
   * show — the door beside the one {@link CaptureEngine.#report} closes.
   */
  #announce(stream: MediaStreamLike): void {
    if (!this.#previewAsked || this.#previewWanted === null) return;
    this.#reported = stream;
    this.#ports.previewChanged?.(stream);
  }

  /** Fork one open stream to the follower and announce it. Returns the detach. */
  #tap(stream: MediaStreamLike): () => void {
    const untap = this.#ports.tapAudio(stream);
    this.#ports.send({ kind: "listening", listening: true });
    return untap;
  }
}

/** Hand the camera and microphone back, so the recording light goes out the
 * moment the take does. */
function release(stream: MediaStreamLike): void {
  for (const track of stream.getTracks()) track.stop();
}

const KINDS = ["video", "audio"] as const;

type Kind = (typeof KINDS)[number];

/** Which devices a request names. */
function kinds(constraints: OpenConstraints): readonly Kind[] {
  return KINDS.filter((kind) => constraints[kind] !== undefined);
}

/** Whether a request names any device at all. `getUserMedia({})` throws, and an
 * empty want means "nothing open" rather than "open nothing". */
function wanting(constraints: OpenConstraints): boolean {
  return kinds(constraints).length > 0;
}

/**
 * Drop the keys an explicit `undefined` left behind, so `{ video: undefined }` and
 * `{}` are the one request they both describe.
 *
 * **`{}` is not `null`, and the difference is load-bearing** (web-ui ticket 06).
 * Null is a host that wants no preview at all — the desktop's whole arrangement,
 * and the half-turn of the green room's Try again. `{}` is a host that wants one
 * and has switched every device off, which is exactly what `captureConstraints`
 * produces from two off toggles.
 *
 * This used to collapse the second into the first, and it cost the preview its
 * authority over rehearsal at the one moment it was most needed: `#wanted` falls
 * back to the standing `#rehearsalWanted` when `#previewWanted` is null, so
 * turning the second switch off re-opened the microphone the first switch had just
 * closed. The bar's circle read off, `MIC_OFF_NOTE` said the take would have no
 * sound, and the tab's microphone indicator was lit with the recogniser reading
 * it.
 */
function normalise(constraints: OpenConstraints | null): OpenConstraints | null {
  if (!constraints) return null;
  const clean: OpenConstraints = {};
  if (constraints.video) clean.video = constraints.video;
  if (constraints.audio) clean.audio = constraints.audio;
  return clean;
}

/**
 * The part of `want` that `have` does not cover — the kinds to go and open when a
 * device is switched back on (web-ui ticket 06).
 *
 * Only meaningful when `subsumes(want, have)`, i.e. `have` is `want` with a
 * device missing, which is the one case it is called in.
 */
function missingFrom(
  have: OpenConstraints,
  want: OpenConstraints,
): OpenConstraints {
  const opening: OpenConstraints = {};
  for (const kind of kinds(want)) {
    if (have[kind] === undefined) opening[kind] = want[kind];
  }
  return opening;
}

/** Whether `want` is `have` with a device switched off — the same constraints for
 * every kind it still names, and fewer of them. */
function subsumes(have: OpenConstraints, want: OpenConstraints): boolean {
  const wanted = kinds(want);
  const held = kinds(have);
  return (
    wanted.length < held.length &&
    wanted.every((kind) => held.includes(kind) && same(want[kind], have[kind]))
  );
}

/**
 * Deep structural equality over the plain objects constraints are made of.
 *
 * Reference equality is no use here: `videoConstraints()` and `audioConstraints()`
 * build a fresh object on every call, so a request a host repeats unchanged is
 * never the same object as the one before it — and this comparison is what decides
 * both whether a take may borrow the preview and whether a re-render costs a
 * device open.
 */
function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => same(item, b[index]))
    );
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every((key) => key in right && same(left[key], right[key]))
  );
}
